import React from 'react';
import { EditHistoryHook } from '../../types';
import { formatDateTimestamp } from '../../utils/formatters';
import Modal from '../ui/Modal';

interface EditHistoryModalProps {
  editHistoryHook: EditHistoryHook;
  isOpen: boolean;
  onClose: () => void;
}

const EditHistoryModal: React.FC<EditHistoryModalProps> = ({
  editHistoryHook,
  isOpen,
  onClose,
}) => {
  if (!editHistoryHook) return null;

  const { editHistory, undo, redo, isLoading } = editHistoryHook;

  const getOperationIcon = (type: string) => {
    switch (type) {
      case 'speaker_edited':
        return '👤';
      case 'notes_edited':
        return '📄';
      case 'summary_edited':
        return '📋';
      default:
        return '✏️';
    }
  };

  const footer = (
    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <button
          onClick={undo}
          disabled={!editHistory?.canUndo || isLoading}
          className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed rounded"
        >
          ← Undo
        </button>
        <button
          onClick={redo}
          disabled={!editHistory?.canRedo || isLoading}
          className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed rounded"
        >
          Redo →
        </button>
      </div>

      <div className="text-sm text-gray-500 dark:text-gray-400">
        {editHistory && (
          <>
            {editHistory.currentIndex + 1} of {editHistory.operations.length}{' '}
            operations
          </>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit History"
      maxWidth="2xl"
      footer={footer}
    >
      <div className="max-h-[60vh] overflow-y-auto">
        {!editHistory || editHistory.operations.length === 0 ? (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
            No edit history available
          </div>
        ) : (
          <div className="space-y-2">
            {editHistory.operations.map((operation: any) => (
              <div
                key={operation.index}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  operation.isCurrent
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : operation.canUndoTo
                      ? 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
                      : 'border-gray-100 dark:border-gray-700 bg-gray-25 dark:bg-gray-900 opacity-60'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">
                      {getOperationIcon(operation.type)}
                    </span>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {operation.summary}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDateTimestamp(operation.timestamp)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {operation.isCurrent && (
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                        Current
                      </span>
                    )}

                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      #{operation.index + 1}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EditHistoryModal;
